import React, { useEffect, useState, useRef, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import strings from "../../../i18n/Strings";
import { ApiUtil } from "../../../util/ApiUtil";
import { showFailureSnackbar } from "../../../store/action/snackbarAction";
import { Grid, Box } from "@material-ui/core";
import withWidth from "@material-ui/core/withWidth";
import { getCompetitionYear, isDateValid } from "../../../util/GeneralUtil";
import "./CompetitionForm.css";
import moment from "moment/moment";
import CompetitionLookupSection from "./CompetitionLookupSection";
import SelectedCompetitionSummary from "./SelectedCompetitionSummary";
import CompetitionPaymentsSection from "./CompetitionPaymentsSection";

const useStyles = makeStyles({
  gridDataText: {
    color: "#7c7c7c",
    fontWeight: 600,
    margin: 1,
  },
  gridHeadDataText: {
    fontWeight: 600,
    margin: 1,
  },
  buttonStyle: {
    background: "#EC5269",
    color: "white",
    textTransform: "none",
    borderRadius: 10,
    width: 160,
    margin: "1%",
  },
  buttonStyleChange: {
    background: "#EC5269",
    color: "white",
    textTransform: "none",
    borderRadius: 10,
    width: 180,
    margin: "1%",
  },
  competitionButtonStyle: {
    background: "#EC5269",
    color: "white",
    textTransform: "none",
    borderRadius: 10,
    width: 200,
    // margin: "1%",
    marginTop: "12px",
  },
  competitionButtonStyleContinue: {
    background: "#EC5269",
    color: "white",
    textTransform: "none",
    borderRadius: 10,
    width: 100,
    // margin: "1%",
    marginTop: "12px",
  },
  competitionButtonStyleSummary: {
    background: "#EC5269",
    color: "white",
    textTransform: "none",
    borderRadius: 10,
    width: 170,
    // margin: "1%",
    marginTop: "12px",
  },
  competitionButton: {
    marginLeft: "50px",
  },
  headerText: {
    color: "#7c7c7c",
    fontWeight: "600",
  },
  headCell: {
    color: "#7c7c7c",
    fontWeight: 600,
    fontSize: 14,
    paddingLeft: 24,
    paddingRight: 24,
    whiteSpace: "nowrap",
  },
  headerDataText: {
    color: "#7c7c7c",
    fontWeight: "500",
    fontSize: 15,
  },
  pdfName: {
    padding: "1px",
    // marginLeft:'8px'
  },
  searchPanel: {
    paddingTop: "20px",
    paddingBottom: "20px",
    marginTop: 8,
    width: "100%",
  },
  sectionTitle: {
    paddingTop: "10px",
    paddingBottom: "10px",
    color: "#7c7c7c",
    fontWeight: "600",
    fontSize: 18,
    margin: "8px 0 12px",
  },
  headRow: {
    padding: "8px 10px",
    color: "#7c7c7c",
    fontWeight: 600,
    fontSize: 18,
  },
  row: {
    padding: "8px 10px",
    fontSize: 16,
  },
  cellPad: {
    paddingLeft: 30,
    fontSize: 16,
  },
  searchFeature: {
    paddingLeft: "12px",
  },
  close: { position: "absolute", right: 8, top: 8 },
  numberInput: {
    "& .MuiInputBase-input": {
      paddingTop: "25px",
    },
  },
  dialogBodySection: {
    backgroundColor: "#F4F6F8",
  },
  lineLength: {
    width: "230px",
  },
});

function CompetitionEvents(props) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const programCompetitionId =
    Number(props.location.state.selectedCompetition?.competitionId) || 0;
  const [competitionEvents, setCompetitionEvents] = useState([]);
  const [competitionEventsBackup, setCompetitionEventsBackup] = useState([]);
  const [total, setTotal] = useState(0);

  const [dataForForm, setDataForForm] = useState({
    competitionId: "",
  });

  const [selectedCompetition, setSelectedCompetition] = useState(null);

  const [formData, setFormData] = useState({
    competitionDate: null,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [loader, setLoader] = useState(false);

  // search / dialog state
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCompIdx, setSelectedCompIdx] = useState(null);
  const [searching, setSearching] = useState(false);
  const [compLookupOpen, setCompLookupOpen] = useState(false);
  const [lastSearchedId, setLastSearchedId] = useState(null);
  const [competitionIdError, setCompetitionIdError] = useState("");

  const hiddenFileInput = useRef(null);

  const dateFormatter = (iso) =>
    iso ? moment(iso, "YYYY-MM-DD", true).format("MM/DD/YYYY") : "";

  const futureDateMatchId = useMemo(() => [13], []);

  // --------------------- Load competition events --------------------- //
  useEffect(() => {
    setLoader(true);
    const id = props.location.state.selectedCompetition.competitionId;
    if (id) {
      ApiUtil.getWithOutToken(`competition/events/${id}`)
        .then((result) => {
          const events = result.data.map((value) => ({
            ...value,
            isChecked: false,
            quantity: 0,
          }));
          setCompetitionEvents(events);
          setCompetitionEventsBackup(events);
        })
        .catch((err) => {
          console.log(err);
          dispatch(showFailureSnackbar("Failed to load events."));
        })
        .finally(() => setLoader(false));
    } else {
      setLoader(false);
    }
  }, [dispatch, props.location.state.selectedCompetition.competitionId]);

  // --------------------- Outreach / search API helpers --------------------- //
  const outreachCompApi = {
    search: async ({
      page = 1,
      size = 25,
      name = "",
      competitionName = "",
      startDate = "",
      competitionId = "",
    } = {}) => {
      const params = new URLSearchParams();
      const finalName = name || competitionName;
      if (finalName.trim()) params.set("competitionName", finalName.trim());
      if (competitionId) params.set("competitionId", String(competitionId));
      if (startDate) params.set("startDate", startDate);
      params.set("page", String(page));
      params.set("size", String(size));

      const res = await ApiUtil.getWithOutToken(
        `competition/outreach_competition?${params.toString()}`
      );

      if (Array.isArray(res.data)) {
        return { rows: res.data, total: res.data.length, paginated: false };
      }
      return {
        rows: res.data.data || [],
        total: Number(res.data.total) || 0,
        paginated: true,
      };
    },
  };

  const CompSearchApi = {
    search: async ({
      compName = "",
      compId = "",
      startDate = "",
      page = 1,
      size = 10,
      selectedCompId,
    } = {}) => {
      if (selectedCompId === 3) {
        const res = await outreachCompApi.search({
          name: compName,
          competitionId: compId,
          startDate,
          page,
          size,
        });

        const rows = (res.rows || []).map((r) => ({
          competition_name: r.competitionName,
          usef_competition_id: r.competitionId,
          start_date: r.startDate,
          end_date: r.endDate,
          comp_year: r.compYear,
          competition_location:
            r.city && r.state
              ? `${r.city}, ${r.state}`
              : r.city || r.state || "",
        }));

        return {
          rows,
          total: res.total ?? rows.length,
          paginated: true,
        };
      }

      const params = new URLSearchParams();
      if (compName.trim()) params.set("name", `${compName.trim()}%`);

      const idStr = String(compId).replace(/\D+/g, "");
      if (idStr) params.set("usef_competition_id", idStr);

      let sd = startDate;
      if (sd instanceof Date && !isNaN(sd)) {
        sd = sd.toISOString().slice(0, 10);
      }
      if (typeof sd === "string" && sd.trim()) {
        params.set("start_date", sd.trim());
      }

      params.set("page", String(page));
      params.set("size", String(size));

      const url = `competition/competition_search?${params.toString()}`;
      const res = await ApiUtil.getWithOutToken(url);

      let rows = [];
      let total = 0;

      if (Array.isArray(res?.data?.data)) {
        rows = res.data.data;
        total = Number(res.data.total) || rows.length;
      } else if (Array.isArray(res?.data)) {
        rows = res.data;
        total = rows.length;
      } else if (Array.isArray(res?.rows)) {
        rows = res.rows;
        total = rows.length;
      }
      return { rows, total, paginated: true };
    },
  };

  // --------------------- Selection + search handlers --------------------- //

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (name === "competitionId") {
      setDataForForm((prev) => ({
        ...prev,
        competitionId: value.replace(/\D+/g, ""),
      }));
      setCompetitionIdError("");
      setShowSearchPanel(false);
      setSearchResults([]);
      setSelectedCompIdx(null);
      setSelectedCompetition(null);
      setFormData((prev) => ({
        ...prev,
        competitionDate: null,
      }));
      return;
    }
  };

  const applySelection = (row) => {
    setCompetitionIdError("");
    const startDate = row.start_date || null;

    const competitionDates =
      row.start_date || row.end_date
        ? `${dateFormatter(row.start_date)}${
            row.start_date && row.end_date ? " - " : ""
          }${dateFormatter(row.end_date)}`
        : "";

    const competition = {
      competitionId: String(row.usef_competition_id ?? row.competitionId ?? ""),
      competitionName: row.competition_name ?? "",
      competitionYear: row.comp_year ?? "",
      competitionStartDate: row.start_date ?? "",
      competitionEndDate: row.end_date ?? "",
      competitionDates,
      competitionLocation: row.competition_location ?? row.location ?? "",
    };
    setSelectedCompetition(competition);
    setDataForForm((prev) => ({
      ...prev,
      competitionId: "",
    }));
    setFormData((prev) => ({
      ...prev,
      competitionDate: startDate,
      competitionName: competition.competitionName,
      competitionId: competition.competitionId,
      competitionLocation: competition.competitionLocation,
    }));

    setShowSearchPanel(false);
    setSearchResults([]);
    setSelectedCompIdx(null);
  };

  const runCompetitionSearch = async () => {
    const raw = String(dataForForm.competitionId ?? "").trim();

    if (!/^\d+$/.test(raw)) {
      setCompetitionIdError("The Competition ID entered is not valid.");
      return;
    }

    const id = Number(raw);
    if (!Number.isSafeInteger(id)) {
      setCompetitionIdError("The Competition ID entered is not valid.");
      return;
    }

    setSearching(true);
    try {
      setShowSearchPanel(false);
      setSearchResults([]);

      let rows = [];
      if (programCompetitionId === 3) {
        const res = await outreachCompApi.search({
          competitionId: id,
          page: 1,
          size: 25,
        });
        rows = (res.rows || []).map((r) => ({
          competition_name: r.competitionName,
          usef_competition_id: r.competitionId,
          start_date: r.startDate,
          end_date: r.endDate,
          competition_location:
            r.city && r.state
              ? `${r.city}, ${r.state}`
              : r.city || r.state || "",
          comp_year: r.compYear,
        }));
      } else {
        const res = await ApiUtil.getWithOutToken(
          `competition/competition_search?usef_competition_id=${id}`
        );
        rows = Array.isArray(res?.data) ? res.data : [];
      }

      const filtered = rows.filter((r) => {
        const rowId =
          r.usef_competition_id != null
            ? Number(r.usef_competition_id)
            : Number(r.competitionId);
        return rowId === id;
      });

      if (filtered.length === 1) {
        applySelection(filtered[0]);
        const r = filtered[0];
        const normalized = {
          ...r,
          competition_location:
            r.competition_location ||
            r.location ||
            [r.city, r.state].filter(Boolean).join(", ") ||
            "",
        };
        applySelection(normalized);
        setLastSearchedId(id);
        return;
      }

      if (filtered.length > 1) {
        setSearchResults(filtered);
        setSelectedCompIdx(null);
        setShowSearchPanel(true);
        setSearchDialogOpen(true);
        setLastSearchedId(id);
        return;
      }

      setCompetitionIdError("The Competition ID entered is not valid.");
    } catch (e) {
      dispatch(showFailureSnackbar("Search failed. Please try again."));
    } finally {
      setSearching(false);
    }
  };

  const handleGoBack = () => {
    props.history.goBack();
  };

  const handleFileUploadClick = () => {
    hiddenFileInput.current?.click();
  };

  const handleFileRemoveClick = (index) => {
    setUploadedDocuments((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
    hiddenFileInput.current && (hiddenFileInput.current.value = "");
  };

  const handleChange = (event) => {
    const selectedFiles = event.target.files || [];
    if (!selectedFiles.length) return;

    const extensions = [
      "pdf",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "txt",
      "csv",
      "dat",
      "png",
      "jpg",
      "jpeg",
    ];

    const existingFiles = new Set(
      (uploadedDocuments || []).map((f) => (f.fileName || "").toLowerCase())
    );

    for (let i = 0; i < selectedFiles.length; i++) {
      const fileToLoad = selectedFiles[i];
      const fileName = fileToLoad.name;
      const type = fileName.split(".").pop()?.toLowerCase() ?? null;

      if (!type || !extensions.includes(type)) {
        dispatch(
          showFailureSnackbar(
            `The upload file format should be any of the following formats ${extensions.join()}`
          )
        );
        continue;
      }

      if (existingFiles.has(fileName.toLowerCase())) {
        dispatch(showFailureSnackbar(`${fileName} has already been added`));
        continue;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = String(e.target.result).split(",")[1];
        const uploadedFile = {
          fileName,
          file: base64,
          mimeType: fileToLoad.type,
        };
        setUploadedDocuments((prev) => [...prev, uploadedFile]);
      };
      reader.readAsDataURL(fileToLoad);
      existingFiles.add(fileName.toLowerCase());
    }
    event.target.value = "";
  };

  // --------------------- Event selection handlers --------------------- //

  const onCheckboxChange = (competitionEvent) => {
    setCompetitionEvents((state) =>
      state.map((value) =>
        value.competitionEventId === competitionEvent.competitionEventId
          ? {
              ...value,
              isChecked: !value.isChecked,
              quantity:
                value.quantity > 0
                  ? value.quantity
                  : competitionEvent.disableQuantityInput
                  ? 1
                  : 0,
            }
          : value
      )
    );
  };

  const onOfferedDidnotFillChange = (competitionEvent) => {
    setCompetitionEvents((state) =>
      state.map((value) =>
        value.competitionEventId === competitionEvent.competitionEventId
          ? {
              ...value,
              offeredDidNotFill: !value.offeredDidNotFill,
            }
          : value
      )
    );
  };

  const handleQunatityInputChange = (competitionEvent, inputValue) => {
    const val = parseFloat(inputValue);
    setCompetitionEvents((state) =>
      state.map((value) =>
        value.competitionEventId === competitionEvent.competitionEventId
          ? { ...value, quantity: val >= 0 ? val : 0 }
          : value
      )
    );
  };

  const handleCategoryChange = (competitionEvent, selectedCategory) => {
    setCompetitionEvents((state) =>
      state.map((value) =>
        value.competitionEventId === competitionEvent.competitionEventId
          ? { ...value, selectedCategory }
          : value
      )
    );
  };

  const handleAddManualAmount = (competitionEvent, inputValue) => {
    const val = parseFloat(inputValue);
    setCompetitionEvents((state) =>
      state.map((value) =>
        value.competitionEventId === competitionEvent.competitionEventId
          ? {
              ...value,
              quantity: val > 0 ? 1 : 0,
              price: val >= 0 ? val : 0,
            }
          : value
      )
    );
  };

  // --------------------- Recalculate total when events change --------------------- //
  useEffect(() => {
    const totalPrice = competitionEvents.reduce(
      (sum, { isChecked, quantity, price, selectedCategory }) =>
        isChecked
          ? sum + (quantity || 0) * (selectedCategory?.price || price || 0)
          : sum,
      0
    );
    setTotal(totalPrice);
  }, [competitionEvents]);

  // --------------------- Update events based on competition date --------------------- //
  // previousCompEventIds is actually compYear -1 (previous years competition)
  useEffect(() => {
    if (!isDateValid(formData.competitionDate)) return;

    let previousCompEventIds = [];
    let currentCompEventIds = [];

    if (programCompetitionId === 1) {
      previousCompEventIds = [69, 70, 71, 72, 73, 74, 75];
      currentCompEventIds = [48, 49, 52, 73];
    }
    if (programCompetitionId === 2) {
      previousCompEventIds = [76, 77, 78, 79, 80, 81, 82];
      currentCompEventIds = [38, 39, 41, 80];
    }
    if (programCompetitionId === 3) {
      previousCompEventIds = [14, 1];
      currentCompEventIds = [1];
    }

    const year = getCompetitionYear(formData.competitionDate);
    const updatedData = competitionEventsBackup
      ?.filter(
        (s) =>
          (year >= 2025 &&
            !currentCompEventIds.includes(s.competitionEventId)) ||
          (year < 2025 && !previousCompEventIds.includes(s.competitionEventId))
      )
      .map((item) => {
        const isFutureDate =
          futureDateMatchId.includes(item.competitionEventId) &&
          moment(formData.competitionDate, "YYYY-MM-DD", true).isSameOrAfter(
            moment(item.updatedStartDate, "YYYY-MM-DD", true)
          );
        return isFutureDate
          ? {
              ...item,
              price: item.updatedPrice,
              description: item.updatedDescription,
            }
          : { ...item };
      });

    setCompetitionEvents(updatedData);
  }, [
    formData.competitionDate,
    competitionEventsBackup,
    programCompetitionId,
    futureDateMatchId,
  ]);

  // --------------------- Submit --------------------- //

  const onSubmit = () => {
    const summary = competitionEvents
      .filter(({ isChecked }) => isChecked)
      .map(
        ({
          competitionEventId,
          selectedCategory,
          quantity,
          name,
          offeredDidNotFill,
          price,
        }) => ({
          competitionEventId,
          selectedCategory,
          offeredDidNotFill,
          quantity,
          name,
          price,
        })
      );

    const quantityCheck = competitionEvents.filter(
      (val) =>
        val.isChecked &&
        !val.offeredDidNotFill &&
        (val.quantity == null || val.quantity < 1)
    );

    if (summary.length < 1) {
      return dispatch(
        showFailureSnackbar(strings.displayText.selectAtLeaseOneItem)
      );
    }

    if (quantityCheck.length) {
      return dispatch(showFailureSnackbar("Input valid number of horses"));
    }

    if (total === 0) {
      return dispatch(
        showFailureSnackbar(
          "If you need to submit Post Comp Report with no items, contact the USHJA office."
        )
      );
    }

    if (!selectedCompetition?.competitionName) {
      return dispatch(
        showFailureSnackbar(strings.displayText.enterTheCompetitionName)
      );
    }

    if (!selectedCompetition?.competitionId) {
      return dispatch(
        showFailureSnackbar(strings.displayText.enterTheCompetitionId)
      );
    }

    if (!formData.competitionDate) {
      return dispatch(
        showFailureSnackbar(strings.displayText.enterTheCompetitionDate)
      );
    }

    if (!isDateValid(formData.competitionDate)) {
      return dispatch(showFailureSnackbar("Enter valid competition date"));
    }

    const checkoutData = {
      items: summary,
      form: props.location.state.selectedCompetition, // program-level info
      formData: {
        ...formData,
        uploadedDocuments,
        competitionName: selectedCompetition.competitionName,
        competitionId: selectedCompetition.competitionId,
        cCompetitionId: `C${selectedCompetition.competitionId}`,
        competitionLocation: selectedCompetition.competitionLocation,
      },
    };

    props.history.push({
      pathname: "/checkout",
      state: {
        checkoutData,
        amount: total,
        type: "openCompetition",
        username: props.location.state.username,
        email: props.location.state.email,
      },
    });
  };

  const handleChangeCompetition = () => {
    setCompLookupOpen(false);
    setSelectedCompetition(null);
    setDataForForm((prev) => ({ ...prev, competitionId: "" }));
    setFormData((prev) => ({ ...prev, competitionDate: null }));
    setSelectedCompIdx(null);
    setSearchResults([]);
  };

  // --------------------- Render --------------------- //

  return (
    <Box mt={2}>
      <Grid container>
        <Grid item xs={1} />
        <Grid item xs={props.width === "xs" ? 12 : 10}>
          <Grid container spacing={3}>
            <CompetitionLookupSection
              classes={classes}
              headerText={props.location.state.selectedCompetition.name}
              dataForForm={dataForForm}
              setDataForForm={setDataForForm}
              handleInputChange={handleInputChange}
              runCompetitionSearch={runCompetitionSearch}
              searching={searching}
              searchDialogOpen={searchDialogOpen}
              setSearchDialogOpen={setSearchDialogOpen}
              searchResults={searchResults}
              selectedCompIdx={selectedCompIdx}
              setSelectedCompIdx={setSelectedCompIdx}
              applySelection={applySelection}
              setSearchResults={setSearchResults}
              compLookupOpen={compLookupOpen}
              setCompLookupOpen={setCompLookupOpen}
              CompSearchApi={CompSearchApi}
              setSelectedCompetition={setSelectedCompetition} // used for clearing
              dateFormatter={dateFormatter}
              competitionButtonClass={classes.competitionButton}
              competitionButtonStyle={classes.competitionButtonStyle}
              sectionTitleClass={classes.sectionTitle}
              setShowSearchPanel={setShowSearchPanel}
              selectedCompId={programCompetitionId}
              competitionIdError={competitionIdError}
              selectedCompetition={selectedCompetition}
            />

            {selectedCompetition?.competitionId && (
              <>
                <SelectedCompetitionSummary
                  classes={classes}
                  selectedCompetition={selectedCompetition}
                  onChangeCompetition={handleChangeCompetition}
                />

                {/* Payments: programCompetitionId decides which config */}
                {(programCompetitionId === 1 ||
                  programCompetitionId === 2 ||
                  programCompetitionId === 3) && (
                  <CompetitionPaymentsSection
                    classes={classes}
                    competitionEvents={competitionEvents}
                    selectedCompetition={selectedCompetition}
                    selectedCompetitionId={programCompetitionId}
                    total={total}
                    onCheckboxChange={onCheckboxChange}
                    onOfferedDidnotFillChange={onOfferedDidnotFillChange}
                    handleQunatityInputChange={handleQunatityInputChange}
                    handleAddManualAmount={handleAddManualAmount}
                    handleCategoryChange={handleCategoryChange}
                    uploadedDocuments={uploadedDocuments}
                    handleFileUploadClick={handleFileUploadClick}
                    hiddenFileInput={hiddenFileInput}
                    handleChange={handleChange}
                    handleFileRemoveClick={handleFileRemoveClick}
                    handleGoBack={handleGoBack}
                    onSubmit={onSubmit}
                  />
                )}
              </>
            )}
          </Grid>
        </Grid>
        <Grid item xs={1} />
      </Grid>
    </Box>
  );
}

CompetitionEvents.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
};

export default withWidth()(CompetitionEvents);
